import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  TextField,
  CircularProgress,
  Alert,
  Snackbar,
  Divider,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  Grid,
  List,
  ListItem,
  ListItemText,
  DialogTitle,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import DetailsCopyLink from "../../assets/icons/DetailsCopyLink.svg";
import {
  fetchScriptDetails,
  generateVideo,
  updateScript,
  fetchPromptTemplates,
  regenerateScript,
  regenerateScriptWithCustomPrompt,
} from "../../services/api";

// Default avatars and voices - these would typically come from your backend
const AVATARS = [
  { id: "Gala_standing_businesssofa_front_close", name: "Gala (Business)" },
  // { id: "Anna_halflength_brightOffice_front", name: "Anna (Office)" },
  // { id: "Sam_standing_studioLighting_front_far", name: "Sam (Studio)" },
  // { id: "Daniel_halflength_darkGradient_front", name: "Daniel (Dark)" },
];

const VOICES = [
  { id: "b97d1cce77094bc596d7a5e8c06daddb", name: "Emma (Female)" },
  // { id: "455af2320be94075aad4e9350c181c8d", name: "John (Male)" },
  // { id: "f1c76270c17b4aeda12a95110d619a2c", name: "Lisa (Female)" },
  // { id: "c9a97a94384c4a7d96a3d42ef8570bc6", name: "Mark (Male)" },
];

const ScriptDetailModal = ({ open, handleClose, scriptId }) => {
  const navigate = useNavigate();
  const [script, setScript] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedScript, setEditedScript] = useState("");
  const [saveLoading, setSaveLoading] = useState(false);

  // Video generation state
  const [videoGenLoading, setVideoGenLoading] = useState(false);
  const [videoGenError, setVideoGenError] = useState(null);
  const [videoGenSuccess, setVideoGenSuccess] = useState(false);
  const [showVideoOptions, setShowVideoOptions] = useState(false);

  // Video generation options
  const [videoOptions, setVideoOptions] = useState({
    avatar_id: "Gala_standing_businesssofa_front_close",
    voice_id: "b97d1cce77094bc596d7a5e8c06daddb",
    orientation: "portrait",
    speed: 1.0,
  });

  // State for notifications
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  // Prompt related states
  const [showPromptDialog, setShowPromptDialog] = useState(false);
  const [promptsLoading, setPromptsLoading] = useState(false);
  const [promptsError, setPromptsError] = useState(null);
  const [promptTemplates, setPromptTemplates] = useState([]);
  const [selectedPrompt, setSelectedPrompt] = useState(null);

  // Add state for script regeneration
  const [regenerateLoading, setRegenerateLoading] = useState(false);
  const [regenerateError, setRegenerateError] = useState(null);

  // Add state for custom prompt editing
  const [isEditingPrompt, setIsEditingPrompt] = useState(false);
  const [customPrompt, setCustomPrompt] = useState("");
  const [useCustomPrompt, setUseCustomPrompt] = useState(false);

  // Fetch script details when modal opens
  useEffect(() => {
    if (open && scriptId) {
      setLoading(true);
      setError(null);
      setVideoGenSuccess(false);
      setVideoGenError(null);
      setRegenerateError(null);
      setIsEditingPrompt(false);
      setCustomPrompt("");
      setUseCustomPrompt(false);

      fetchScriptDetails(scriptId)
        .then((data) => {
          setScript(data);
          setLoading(false);
        })
        .catch((err) => {
          console.error("Error fetching script details:", err);
          setError(
            `Failed to load script details: ${err.message || "Unknown error"}`
          );
          setLoading(false);
        });
    } else {
      // Reset state when modal closes
      setScript(null);
      setShowVideoOptions(false);
      setSelectedPrompt(null);
      setRegenerateError(null);
      setIsEditingPrompt(false);
      setCustomPrompt("");
      setUseCustomPrompt(false);
    }
  }, [open, scriptId]);

  // Format date from API
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Handle copy link functionality
  const handleCopyLink = (text) => {
    navigator.clipboard
      .writeText(text || `Script ID: ${scriptId}`)
      .then(() => {
        setNotification({
          open: true,
          message: "Script content copied to clipboard!",
          severity: "success",
        });
      })
      .catch((err) => {
        setNotification({
          open: true,
          message: "Failed to copy: " + err,
          severity: "error",
        });
      });
  };

  // Handle video options change
  const handleVideoOptionChange = (e) => {
    const { name, value } = e.target;
    setVideoOptions({
      ...videoOptions,
      [name]: name === "speed" ? parseFloat(value) : value,
    });
  };

  // Handle generate video click
  const handleGenerateVideo = async () => {
    try {
      setVideoGenLoading(true);
      setVideoGenError(null);
      setVideoGenSuccess(false);

      // Create payload for video generation
      const payload = {
        script_id: parseInt(scriptId), // Ensure it's a number
        ...videoOptions,
      };

      // Call API to generate video
      const response = await generateVideo(payload);

      setVideoGenLoading(false);
      setVideoGenSuccess(true);

      // Show success notification
      setNotification({
        open: true,
        message:
          "Video generation started! Check the Videos section to see your video once it's ready.",
        severity: "success",
      });

      // Hide options after successful generation
      setShowVideoOptions(false);

      // Close the modal
      handleClose();

      // Redirect to videos page after a short delay
      setTimeout(() => {
        navigate("/videos");
      }, 1500);
    } catch (err) {
      console.error("Error generating video:", err);
      setVideoGenLoading(false);

      // Check if this is the "already processing" error
      if (err.isAlreadyProcessing) {
        const message =
          "This script already has a video that is generated or currently processing. Please check the Videos page.";

        // Set error with the message
        setVideoGenError(message);

        // Show user-friendly notification with larger font
        setNotification({
          open: true,
          message,
          severity: "info",
        });

        // No redirection dialog anymore
      } else {
        // Handle other errors
        setVideoGenError(
          `Failed to generate video: ${err.message || "Unknown error"}`
        );

        // Show error notification
        setNotification({
          open: true,
          message: `Video generation failed: ${err.message || "Unknown error"}`,
          severity: "error",
        });
      }
    }
  };

  // Handle close notification
  const handleCloseNotification = () => {
    setNotification({
      ...notification,
      open: false,
    });
  };

  // Handle edit button click
  const handleEditClick = () => {
    setIsEditing(true);
    setEditedScript(script.script);
  };

  // Handle save changes
  const handleSaveChanges = async () => {
    try {
      setSaveLoading(true);
      setError(null);

      const updatedScript = await updateScript(scriptId, {
        script: editedScript,
      });

      // Update local state with the new script content
      setScript({
        ...script,
        script: updatedScript.script,
      });

      setIsEditing(false);
      setNotification({
        open: true,
        message: "Script updated successfully!",
        severity: "success",
      });
    } catch (err) {
      console.error("Error updating script:", err);
      setError(`Failed to update script: ${err.message || "Unknown error"}`);
      setNotification({
        open: true,
        message: `Failed to update script: ${err.message || "Unknown error"}`,
        severity: "error",
      });
    } finally {
      setSaveLoading(false);
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedScript(script.script);
  };

  // Fetch prompt templates
  const handleSelectPrompts = async () => {
    try {
      setPromptsLoading(true);
      setPromptsError(null);

      const response = await fetchPromptTemplates();

      if (response && response.results) {
        setPromptTemplates(response.results);
      } else {
        throw new Error("No prompt templates found");
      }

      setShowPromptDialog(true);
    } catch (err) {
      console.error("Error fetching prompt templates:", err);
      setPromptsError(
        `Failed to fetch prompt templates: ${err.message || "Unknown error"}`
      );

      // Show error notification
      setNotification({
        open: true,
        message: `Failed to fetch prompt templates: ${
          err.message || "Unknown error"
        }`,
        severity: "error",
      });
    } finally {
      setPromptsLoading(false);
    }
  };

  // Handle prompt selection
  const handlePromptSelect = (prompt) => {
    // Verify that the prompt has the required prompt field
    if (!prompt.prompt) {
      console.error(
        "Selected prompt template is missing the 'prompt' field:",
        prompt
      );
      setNotification({
        open: true,
        message: "Error: Selected prompt template is missing required data",
        severity: "error",
      });
      return;
    }
    setSelectedPrompt(prompt);
    setCustomPrompt(prompt.prompt); // Initialize custom prompt with the selected template
    setUseCustomPrompt(false); // Reset to using the original prompt by default
  };

  // Handle prompt dialog close
  const handlePromptDialogClose = () => {
    setShowPromptDialog(false);
  };

  // Handle prompt selection and close dialog
  const handleApplySelectedPrompt = () => {
    // Close the prompt dialog
    setShowPromptDialog(false);

    // Reset custom prompt state
    setIsEditingPrompt(false);
    setUseCustomPrompt(false);

    // Show notification about selected prompt
    if (selectedPrompt) {
      setCustomPrompt(selectedPrompt.prompt); // Ensure custom prompt is synced with selected template

      setNotification({
        open: true,
        message: `Prompt "${selectedPrompt.name}" selected. You can now regenerate the script.`,
        severity: "success",
      });

      // Scroll to the regenerate button to make it visible to the user
      setTimeout(() => {
        const regenerateBtn = document.getElementById("regenerate-script-btn");
        if (regenerateBtn) {
          regenerateBtn.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 300);
    }
  };

  // Handle regenerate script
  const handleRegenerateScript = async () => {
    if (!selectedPrompt || !script) {
      setNotification({
        open: true,
        message: "Please select a prompt template first",
        severity: "warning",
      });
      return;
    }

    // Double-check that the selected prompt has the required 'prompt' field
    if (!selectedPrompt.prompt) {
      console.error(
        "Selected prompt is missing the 'prompt' field:",
        selectedPrompt
      );
      setNotification({
        open: true,
        message: "Error: Selected prompt is missing required data",
        severity: "error",
      });
      return;
    }

    try {
      setRegenerateLoading(true);
      setRegenerateError(null);

      // Call the API to regenerate the script
      const response = await regenerateScript(script, selectedPrompt);

      // Handle different possible response formats
      let updatedScriptContent = null;

      if (response && typeof response === "object") {
        // Try to extract script content from various possible response formats
        if (response.script && typeof response.script === "string") {
          // If response.script is a string, use it directly
          updatedScriptContent = response.script;
        } else if (response.script && response.script.script) {
          // If response.script is an object with a script property
          updatedScriptContent = response.script.script;
        } else if (response.new_script) {
          // Try alternate field name
          updatedScriptContent = response.new_script;
        } else if (typeof response.content === "string") {
          // Another possible field name
          updatedScriptContent = response.content;
        }
      }

      if (updatedScriptContent) {
        // Update the script content while preserving other properties
        const updatedScript = {
          ...script,
          script: updatedScriptContent,
        };

        // Update the state with the new script
        setScript(updatedScript);

        // Show success notification
        setNotification({
          open: true,
          message: "Script regenerated successfully!",
          severity: "success",
        });
      } else {
        console.error("Invalid response format:", response);
        throw new Error(
          "Invalid response format - no script content found in the response"
        );
      }
    } catch (err) {
      console.error("Error regenerating script:", err);
      let errorMessage = err.message || "Unknown error";

      // Extract error details from API response if available
      if (err.response) {
        errorMessage += ` (Status: ${err.response.status})`;
        if (err.response.data && err.response.data.detail) {
          errorMessage += ` - ${err.response.data.detail}`;
        }
      }

      setRegenerateError(`Failed to regenerate script: ${errorMessage}`);

      // Show error notification
      setNotification({
        open: true,
        message: `Failed to regenerate script: ${errorMessage}`,
        severity: "error",
      });
    } finally {
      setRegenerateLoading(false);
    }
  };

  // Handle regenerate script with custom prompt
  const handleRegenerateWithCustomPrompt = async () => {
    if (!script) {
      setNotification({
        open: true,
        message: "Script data is missing",
        severity: "error",
      });
      return;
    }

    if (!customPrompt || customPrompt.trim() === "") {
      setNotification({
        open: true,
        message: "Please enter a prompt",
        severity: "warning",
      });
      return;
    }

    try {
      setRegenerateLoading(true);
      setRegenerateError(null);

      // Call the API to regenerate the script with custom prompt
      const response = await regenerateScriptWithCustomPrompt(
        script,
        customPrompt,
        selectedPrompt // Pass the original template for reference
      );

      // Handle different possible response formats
      let updatedScriptContent = null;

      if (response && typeof response === "object") {
        // Try to extract script content from various possible response formats
        if (response.script && typeof response.script === "string") {
          updatedScriptContent = response.script;
        } else if (response.script && response.script.script) {
          updatedScriptContent = response.script.script;
        } else if (response.new_script) {
          updatedScriptContent = response.new_script;
        } else if (typeof response.content === "string") {
          updatedScriptContent = response.content;
        }
      }

      if (updatedScriptContent) {
        // Update the script content while preserving other properties
        const updatedScript = {
          ...script,
          script: updatedScriptContent,
        };

        // Update the state with the new script
        setScript(updatedScript);

        // Exit editing mode
        setIsEditingPrompt(false);

        // Show success notification
        setNotification({
          open: true,
          message: "Script regenerated successfully with custom prompt!",
          severity: "success",
        });
      } else {
        console.error("Invalid response format:", response);
        throw new Error(
          "Invalid response format - no script content found in the response"
        );
      }
    } catch (err) {
      console.error("Error regenerating script with custom prompt:", err);
      let errorMessage = err.message || "Unknown error";

      // Extract error details from API response if available
      if (err.response) {
        errorMessage += ` (Status: ${err.response.status})`;
        if (err.response.data && err.response.data.detail) {
          errorMessage += ` - ${err.response.data.detail}`;
        }
      }

      setRegenerateError(`Failed to regenerate script: ${errorMessage}`);

      // Show error notification
      setNotification({
        open: true,
        message: `Failed to regenerate script: ${errorMessage}`,
        severity: "error",
      });
    } finally {
      setRegenerateLoading(false);
    }
  };

  // Early return if no script data while loading
  if (!open) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        PaperProps={{
          sx: {
            width: "700px",
            // height: "736px",
            height: "936px",
            borderRadius: "8px",
            p: 0,
            m: 0,
            overflowY: "visible",
          },
        }}
      >
        {/* Header with title and close button */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pt: "31px",
            pb: "32px",
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: "bold",
              fontSize: "24px",
              textAlign: "center",
              width: "100%",
            }}
          >
            Script Detail
          </Typography>
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{
              color: "#888",
              position: "absolute",
              right: 16,
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent
          sx={{ p: 0, m: 0, "&::-webkit-scrollbar": { display: "none" } }}
        >
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "500px",
              }}
            >
              <CircularProgress />
            </Box>
          ) : error ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
              <Button
                variant="contained"
                onClick={() => {
                  setLoading(true);
                  setError(null);
                  fetchScriptDetails(scriptId)
                    .then((data) => {
                      setScript(data);
                      setLoading(false);
                    })
                    .catch((err) => {
                      setError(
                        `Failed to load script details: ${
                          err.message || "Unknown error"
                        }`
                      );
                      setLoading(false);
                    });
                }}
              >
                Try Again
              </Button>
            </Box>
          ) : script ? (
            <Box sx={{ p: 4, pt: 3 }}>
              {/* Created, ID, and Headline in one row */}
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  mb: 4,
                  border: "1px solid #F1F5F9",
                  borderRadius: "8px",
                  p: 2,
                }}
              >
                {/* Created Date */}
                <Box sx={{ mr: 2, mb: 2, flex: "1 1 200px" }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#B7B7B7",
                      fontSize: "12px",
                      fontWeight: "500",
                      mb: 1,
                    }}
                  >
                    CREATED
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <CalendarTodayIcon
                      sx={{ fontSize: 18, color: "#6B7280", mr: 1 }}
                    />
                    <Typography sx={{ color: "#111827", fontSize: "14px" }}>
                      {formatDate(script.created_at)}
                    </Typography>
                  </Box>
                </Box>

                {/* ID */}
                <Box sx={{ mr: 2, mb: 2, flex: "1 1 100px" }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#B7B7B7",
                      fontSize: "12px",
                      fontWeight: "500",
                      mb: 1,
                    }}
                  >
                    SCRIPT ID
                  </Typography>
                  <Typography sx={{ color: "#111827", fontSize: "14px" }}>
                    {script.id}
                  </Typography>
                </Box>

                {/* Article ID */}
                <Box sx={{ mr: 2, mb: 2, flex: "1 1 120px" }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#B7B7B7",
                      fontSize: "12px",
                      fontWeight: "500",
                      mb: 1,
                    }}
                  >
                    ARTICLE ID
                  </Typography>
                  <Typography sx={{ color: "#111827", fontSize: "14px" }}>
                    {script.article}
                  </Typography>
                </Box>
              </Box>

              {/* Headline Section (with black label and link icon) */}
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#161616",
                    fontWeight: "600",
                    mb: 1,
                    fontSize: "14px",
                  }}
                >
                  Headline
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    pb: 2,
                    borderBottom: "1px solid #E5E7EB",
                  }}
                >
                  <Typography
                    sx={{ color: "#4B5563", flex: 1, fontSize: "15px" }}
                  >
                    {script.name || "Untitled Script"}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => handleCopyLink(script.name)}
                  >
                    <img src={DetailsCopyLink} alt="copy link" />
                  </IconButton>
                </Box>
              </Box>

              {/* Summary Section */}
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#161616",
                    fontWeight: "600",
                    mb: 1,
                    fontSize: "14px",
                  }}
                >
                  Summary
                </Typography>
                <Box
                  sx={{
                    color: "#4B5563",
                    fontSize: "14px",
                    lineHeight: "1.5",
                    maxHeight: "140px",
                    overflowY: "auto",
                    mb: 2,
                    pr: 1,
                    borderBottom: "1px solid #E5E7EB",
                    pb: 2,
                    "&::-webkit-scrollbar": {
                      width: "6px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "#bbb",
                      borderRadius: "6px",
                    },
                  }}
                >
                  {script.summary ? (
                    script.summary.split("\n").map((paragraph, index) => (
                      <Typography
                        key={index}
                        variant="body2"
                        paragraph
                        sx={{ fontSize: "14px", mb: 1 }}
                      >
                        {paragraph}
                      </Typography>
                    ))
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{ fontSize: "14px", fontStyle: "italic" }}
                    >
                      No summary available
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Script Content Section */}
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#161616",
                    fontWeight: "600",
                    mb: 1,
                    fontSize: "14px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span>Script Content</span>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => handleCopyLink(script.script)}
                    sx={{
                      fontSize: "12px",
                      color: "#4691E1",
                      textTransform: "none",
                    }}
                  >
                    Copy Content
                  </Button>
                </Typography>
                <TextField
                  multiline
                  rows={8}
                  fullWidth
                  value={
                    isEditing
                      ? editedScript
                      : script.script || "No content available for this script."
                  }
                  onChange={(e) => setEditedScript(e.target.value)}
                  InputProps={{
                    readOnly: !isEditing,
                    sx: regenerateLoading
                      ? {
                          backgroundColor: "#F9FAFB",
                          opacity: 0.8,
                        }
                      : {},
                  }}
                  sx={{
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      fontSize: "14px",
                    },
                  }}
                />
                {regenerateLoading && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      position: "relative",
                      mt: -10,
                      mb: 8,
                      zIndex: 1,
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: "rgba(255, 255, 255, 0.8)",
                        borderRadius: "8px",
                        p: 2,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                      }}
                    >
                      <CircularProgress sx={{ mb: 1 }} />
                      <Typography variant="body2" sx={{ fontWeight: "500" }}>
                        Regenerating script...
                      </Typography>
                    </Box>
                  </Box>
                )}
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  {isEditing ? (
                    <>
                      <Button
                        variant="outlined"
                        onClick={handleCancelEdit}
                        sx={{
                          textTransform: "none",
                          borderRadius: "6px",
                          color: "#16161699",
                          fontWeight: "500",
                          boxShadow: "none",
                          px: 3,
                          py: 1,
                          fontSize: "12px",
                          mr: 2,
                          border: "1px solid #16161699",
                          minWidth: "100px",
                          "&:hover": {
                            bgcolor: "#F9FAFB",
                            border: "1px solid #16161699",
                          },
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleSaveChanges}
                        disabled={saveLoading}
                        startIcon={
                          saveLoading ? <CircularProgress size={20} /> : null
                        }
                        sx={{
                          textTransform: "none",
                          borderRadius: "6px",
                          bgcolor: "#378DEA",
                          color: "#FFFFFF",
                          fontWeight: "500",
                          boxShadow: "none",
                          px: 3,
                          py: 1,
                          fontSize: "12px",
                          minWidth: "140px",
                          "&:hover": {
                            bgcolor: "#1565c0",
                            boxShadow: "none",
                          },
                        }}
                      >
                        {saveLoading ? "Saving..." : "Save Changes"}
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        variant="outlined"
                        onClick={handleEditClick}
                        disabled={regenerateLoading}
                        sx={{
                          textTransform: "none",
                          borderRadius: "6px",
                          color: "#16161699",
                          fontWeight: "500",
                          boxShadow: "none",
                          px: 3,
                          py: 1,
                          fontSize: "12px",
                          mr: 2,
                          border: "1px solid #16161699",
                          minWidth: "100px",
                          "&:hover": {
                            bgcolor: "#F1F5F9",
                            border: "1px solid #16161699",
                          },
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={handleSelectPrompts}
                        disabled={promptsLoading || regenerateLoading}
                        startIcon={
                          promptsLoading ? <CircularProgress size={20} /> : null
                        }
                        sx={{
                          textTransform: "none",
                          borderRadius: "6px",
                          mr: 2,
                          color: "#378DEA",
                          fontWeight: "500",
                          boxShadow: "none",
                          px: 3,
                          py: 1,
                          fontSize: "12px",
                          border: "1px solid #378DEA",
                          minWidth: "140px",
                          "&:hover": {
                            bgcolor: "#F1F5F9",
                            border: "1px solid #378DEA",
                          },
                        }}
                      >
                        {promptsLoading ? "Loading..." : "Select Prompts"}
                      </Button>
                      <Button
                        variant="contained"
                        onClick={() => setShowVideoOptions(!showVideoOptions)}
                        disabled={regenerateLoading}
                        sx={{
                          textTransform: "none",
                          borderRadius: "6px",
                          bgcolor: "#378DEA",
                          color: "#FFFFFF",
                          fontWeight: "500",
                          boxShadow: "none",
                          px: 3,
                          py: 1,
                          fontSize: "12px",
                          minWidth: "140px",
                          "&:hover": {
                            bgcolor: "#1565c0",
                            boxShadow: "none",
                          },
                        }}
                      >
                        {showVideoOptions ? "Hide Options" : "Generate Video"}
                      </Button>
                    </>
                  )}
                </Box>

                {/* Regenerate Script button shown when a prompt is selected */}
                {!isEditing && selectedPrompt && (
                  <Box sx={{ mt: 2, width: "100%" }}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        border: "1px solid #E5E7EB",
                        borderRadius: "8px",
                        mb: 2,
                        bgcolor: "#F1F5F9",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 1,
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: "600", color: "#111827" }}
                        >
                          {useCustomPrompt
                            ? "Custom Prompt"
                            : `Selected Prompt: ${selectedPrompt.name}`}
                        </Typography>
                        <Box>
                          {isEditingPrompt ? (
                            <>
                              <Button
                                variant="text"
                                size="small"
                                onClick={() => {
                                  setIsEditingPrompt(false);
                                  setCustomPrompt(selectedPrompt.prompt);
                                  setUseCustomPrompt(false);
                                }}
                                sx={{
                                  fontSize: "12px",
                                  color: "#6B7280",
                                  textTransform: "none",
                                  p: 0,
                                  minWidth: "auto",
                                  mr: 2,
                                }}
                              >
                                Cancel
                              </Button>
                              <Button
                                variant="text"
                                size="small"
                                onClick={() => {
                                  setIsEditingPrompt(false);
                                  setUseCustomPrompt(true);

                                  setNotification({
                                    open: true,
                                    message:
                                      "Custom prompt saved. Click 'Regenerate' to apply it.",
                                    severity: "success",
                                  });
                                }}
                                sx={{
                                  fontSize: "12px",
                                  color: "#22C55E",
                                  textTransform: "none",
                                  p: 0,
                                  minWidth: "auto",
                                }}
                              >
                                Save
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button
                                variant="text"
                                size="small"
                                onClick={handleSelectPrompts}
                                sx={{
                                  fontSize: "12px",
                                  color: "#4691E1",
                                  textTransform: "none",
                                  p: 0,
                                  minWidth: "auto",
                                  mr: 2,
                                }}
                              >
                                Change
                              </Button>
                              <Button
                                variant="text"
                                size="small"
                                onClick={() => setIsEditingPrompt(true)}
                                sx={{
                                  fontSize: "12px",
                                  color: "#4691E1",
                                  textTransform: "none",
                                  p: 0,
                                  minWidth: "auto",
                                }}
                              >
                                Edit
                              </Button>
                            </>
                          )}
                        </Box>
                      </Box>

                      {isEditingPrompt ? (
                        <TextField
                          multiline
                          fullWidth
                          rows={6}
                          value={customPrompt}
                          onChange={(e) => setCustomPrompt(e.target.value)}
                          placeholder="Enter your custom prompt here..."
                          variant="outlined"
                          sx={{
                            mt: 1,
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "#FFFFFF",
                              fontSize: "13px",
                            },
                          }}
                        />
                      ) : (
                        <Accordion
                          elevation={0}
                          disableGutters
                          sx={{
                            "&:before": {
                              display: "none",
                            },
                            backgroundColor: "transparent",
                            border: "1px dashed #E5E7EB",
                            borderRadius: "6px",
                            overflow: "hidden",
                          }}
                        >
                          <AccordionSummary
                            expandIcon={
                              <ExpandMoreIcon
                                sx={{ color: "#6B7280", fontSize: "18px" }}
                              />
                            }
                            sx={{
                              p: 1,
                              backgroundColor: "#E5E7EB20",
                              "&.Mui-expanded": {
                                minHeight: "40px",
                              },
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: "13px",
                                  color: "#4B5563",
                                  fontWeight: "500",
                                }}
                              >
                                View Prompt Content
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: "11px",
                                  color: "#6B7280",
                                  ml: 1,
                                }}
                              >
                                (Click to expand)
                              </Typography>
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails sx={{ p: 1.5, pt: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: "13px",
                                color: "#4B5563",
                                whiteSpace: "pre-wrap",
                                lineHeight: "1.5",
                              }}
                            >
                              {useCustomPrompt
                                ? customPrompt
                                : selectedPrompt.prompt}
                            </Typography>
                          </AccordionDetails>
                        </Accordion>
                      )}
                    </Paper>

                    <Button
                      id="regenerate-script-btn"
                      variant="contained"
                      onClick={() => {
                        if (useCustomPrompt) {
                          handleRegenerateWithCustomPrompt();
                        } else {
                          handleRegenerateScript();
                        }
                      }}
                      disabled={regenerateLoading || isEditingPrompt}
                      startIcon={
                        regenerateLoading ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          <RefreshIcon />
                        )
                      }
                      sx={{
                        textTransform: "none",
                        borderRadius: "6px",
                        bgcolor: "#22C55E",
                        color: "#FFFFFF",
                        fontWeight: "500",
                        boxShadow: "none",
                        px: 3,
                        py: 1,
                        fontSize: "14px",
                        width: "100%",
                        height: "40px",
                        "&:hover": {
                          bgcolor: "#16A34A",
                          boxShadow: "none",
                        },
                      }}
                    >
                      {regenerateLoading
                        ? "Regenerating..."
                        : useCustomPrompt
                        ? "Regenerate with Custom Prompt"
                        : "Regenerate with Selected Prompt"}
                    </Button>
                  </Box>
                )}

                {/* Show regeneration error if any */}
                {regenerateError && (
                  <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
                    {regenerateError}
                  </Alert>
                )}
              </Box>

              {/* Video generation options */}
              {showVideoOptions && (
                <Box sx={{ mb: 4, mt: 2 }}>
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontSize: "16px", fontWeight: "600", mb: 2 }}
                    >
                      Video Generation Options
                    </Typography>

                    {videoGenError && (
                      <Alert
                        severity="info"
                        sx={{ mb: 2, fontSize: "16px", fontWeight: "500" }}
                      >
                        {videoGenError}
                      </Alert>
                    )}

                    {videoGenSuccess && (
                      <Alert severity="success" sx={{ mb: 2 }}>
                        Video generation started successfully! Check the Videos
                        tab to see your video once it's ready.
                      </Alert>
                    )}

                    <Grid container spacing={2}>
                      {/* Avatar Selection */}
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small">
                          <InputLabel id="avatar-select-label">
                            Avatar
                          </InputLabel>
                          <Select
                            labelId="avatar-select-label"
                            id="avatar-select"
                            name="avatar_id"
                            value={videoOptions.avatar_id}
                            onChange={handleVideoOptionChange}
                            label="Avatar"
                          >
                            {AVATARS.map((avatar) => (
                              <MenuItem key={avatar.id} value={avatar.id}>
                                {avatar.name}
                              </MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>
                            Choose a presenter for your video
                          </FormHelperText>
                        </FormControl>
                      </Grid>

                      {/* Voice Selection */}
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small">
                          <InputLabel id="voice-select-label">Voice</InputLabel>
                          <Select
                            labelId="voice-select-label"
                            id="voice-select"
                            name="voice_id"
                            value={videoOptions.voice_id}
                            onChange={handleVideoOptionChange}
                            label="Voice"
                          >
                            {VOICES.map((voice) => (
                              <MenuItem key={voice.id} value={voice.id}>
                                {voice.name}
                              </MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>
                            Select a voice for narration
                          </FormHelperText>
                        </FormControl>
                      </Grid>

                      {/* Orientation */}
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small">
                          <InputLabel id="orientation-select-label">
                            Orientation
                          </InputLabel>
                          <Select
                            labelId="orientation-select-label"
                            id="orientation-select"
                            name="orientation"
                            value={videoOptions.orientation}
                            onChange={handleVideoOptionChange}
                            label="Orientation"
                          >
                            <MenuItem value="portrait">Portrait</MenuItem>
                            <MenuItem value="landscape">Landscape</MenuItem>
                          </Select>
                          <FormHelperText>Video orientation</FormHelperText>
                        </FormControl>
                      </Grid>

                      {/* Speed */}
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small">
                          <InputLabel id="speed-select-label">Speed</InputLabel>
                          <Select
                            labelId="speed-select-label"
                            id="speed-select"
                            name="speed"
                            value={videoOptions.speed}
                            onChange={handleVideoOptionChange}
                            label="Speed"
                          >
                            <MenuItem value={0.8}>Slow (0.8x)</MenuItem>
                            <MenuItem value={1.0}>Normal (1.0x)</MenuItem>
                            <MenuItem value={1.2}>Fast (1.2x)</MenuItem>
                          </Select>
                          <FormHelperText>Narration speed</FormHelperText>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Box>

                  <Box
                    sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}
                  >
                    <Button
                      variant="outlined"
                      onClick={() => setShowVideoOptions(false)}
                      sx={{
                        textTransform: "none",
                        borderRadius: "6px",
                        mr: 2,
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleGenerateVideo}
                      disabled={videoGenLoading}
                      startIcon={
                        videoGenLoading ? <CircularProgress size={20} /> : null
                      }
                      sx={{
                        textTransform: "none",
                        borderRadius: "6px",
                        bgcolor: "#378DEA",
                        color: "#FFFFFF",
                        fontWeight: "500",
                        boxShadow: "none",
                        "&:hover": {
                          bgcolor: "#1565c0",
                        },
                      }}
                    >
                      {videoGenLoading ? "Generating..." : "Start Generation"}
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          ) : (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography>Script information is not available</Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Prompt Templates Dialog */}
      <Dialog
        open={showPromptDialog}
        onClose={handlePromptDialogClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: "700px",
            borderRadius: "8px",
          },
        }}
      >
        <DialogTitle
          sx={{
            p: 3,
            borderBottom: "1px solid #E5E7EB",
            fontSize: "18px",
            fontWeight: "600",
            color: "#111827",
          }}
        >
          Select a Prompt Template
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {promptsError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {promptsError}
            </Alert>
          )}

          <Typography variant="body2" sx={{ mb: 2, mt: 2, color: "#6B7280" }}>
            Select a prompt template to regenerate your script. Click the expand
            icon to view the full prompt content.
          </Typography>

          {promptsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <List sx={{ pt: 0 }}>
              {promptTemplates.map((prompt) => (
                <Paper
                  key={prompt.id}
                  elevation={0}
                  sx={{
                    mb: 2,
                    border:
                      selectedPrompt && selectedPrompt.id === prompt.id
                        ? "1px solid #378DEA"
                        : "1px solid #E5E7EB",
                    borderRadius: "8px",
                    overflow: "hidden",
                    backgroundColor:
                      selectedPrompt && selectedPrompt.id === prompt.id
                        ? "#F1F5F9"
                        : "white",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      borderColor:
                        selectedPrompt && selectedPrompt.id === prompt.id
                          ? "#378DEA"
                          : "#CBD5E1",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                    },
                  }}
                >
                  <Accordion
                    elevation={0}
                    disableGutters
                    sx={{
                      "&:before": {
                        display: "none",
                      },
                      backgroundColor: "transparent",
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ color: "#6B7280" }} />}
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        px: 2,
                        py: 1.5,
                        "&.Mui-expanded": {
                          minHeight: "48px",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "flex-start",
                          flex: 1,
                          cursor: "pointer",
                        }}
                        onClick={() => handlePromptSelect(prompt)}
                      >
                        {selectedPrompt && selectedPrompt.id === prompt.id ? (
                          <CheckCircleIcon
                            sx={{
                              color: "#378DEA",
                              mr: 1.5,
                              mt: 0.5,
                              fontSize: "20px",
                              flexShrink: 0,
                            }}
                          />
                        ) : (
                          <RadioButtonUncheckedIcon
                            sx={{
                              color: "#6B7280",
                              mr: 1.5,
                              mt: 0.5,
                              fontSize: "20px",
                              flexShrink: 0,
                            }}
                          />
                        )}
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: "600",
                              color:
                                selectedPrompt &&
                                selectedPrompt.id === prompt.id
                                  ? "#378DEA"
                                  : "#111827",
                            }}
                          >
                            {prompt.name}
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mt: 0.5,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color: "#6B7280",
                                fontSize: "13px",
                              }}
                            >
                              Click to select • Expand to view full prompt
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails
                      sx={{
                        px: 2,
                        pt: 0,
                        pb: 2,
                        borderTop: "1px dashed #E5E7EB",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          whiteSpace: "pre-wrap",
                          color: "#4B5563",
                          lineHeight: 1.6,
                        }}
                      >
                        {prompt.prompt}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Paper>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5, borderTop: "1px solid #E5E7EB" }}>
          <Button
            onClick={handlePromptDialogClose}
            variant="outlined"
            sx={{
              textTransform: "none",
              borderRadius: "6px",
              color: "#16161699",
              fontWeight: "500",
              px: 3,
              py: 1,
              minWidth: "100px",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleApplySelectedPrompt}
            variant="contained"
            disabled={!selectedPrompt}
            sx={{
              textTransform: "none",
              borderRadius: "6px",
              bgcolor: "#378DEA",
              color: "#FFFFFF",
              fontWeight: "500",
              boxShadow: "none",
              px: 3,
              py: 1,
              minWidth: "100px",
              "&:hover": {
                bgcolor: "#1565c0",
                boxShadow: "none",
              },
            }}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification with larger font size for errors */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{
            width: "100%",
            "& .MuiAlert-message": {
              fontSize:
                notification.severity === "info" ||
                notification.severity === "error"
                  ? "16px"
                  : "14px",
              fontWeight:
                notification.severity === "info" ||
                notification.severity === "error"
                  ? "500"
                  : "normal",
            },
          }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ScriptDetailModal;
